import type { AxiosError, AxiosResponse } from 'axios';
import { notifications } from '@mantine/notifications';

/**
 * Demo accounts (backend domain/demo_account): CMS-created logins on a fake
 * CEISA company, open for a limited time after the first login, with a small
 * extraction quota. Nothing they submit reaches CEISA.
 *
 * The error codes mirror backend domain/demo_account/constants — keep in sync.
 */
export const DEMO_ERROR = {
  expired: 'DEMO_EXPIRED',
  quotaExceeded: 'DEMO_QUOTA_EXCEEDED',
  restricted: 'DEMO_RESTRICTED',
} as const;

/** Set on the response to a demo account's CEISA submission. */
export const DEMO_SIMULATED_HEADER = 'x-jarvis-demo-simulated';

/** Query flag the login page reads to explain why the session ended. */
export const DEMO_ENDED_PARAM = 'demo';
export const DEMO_ENDED_VALUE = 'ended';

export type DemoDocType = 'PIB' | 'PEB' | 'MANIFEST';
export type DemoUsage = {
  status: 'pending' | 'active' | 'expired' | 'deactivated';
  expiresAt: string;
  quota: Record<DemoDocType, { used: number; limit: number }>;
  /** The prospect's answers, or (until submitted) what sales typed in the CMS. */
  profile?: DemoProfile;
  profileSubmitted?: boolean;
};

/** Monthly volume choices; mirror backend domain/demo_account/constants. */
export const DEMO_VOLUMES = ['lt50', '50-200', 'gt200'] as const;
export const DEMO_INTERESTS = ['PIB', 'PEB', 'MANIFEST', 'TRACKING', 'OTHER'] as const;

/** The demo account's first onboarding step: who the prospect is. */
export type DemoProfile = {
  name: string;
  company: string;
  email: string;
  phone: string;
  position: string;
  volume: string;
  interests: string[];
};

/** Shared by the banner and the onboarding profile step. */
export const DEMO_ME_QUERY_KEY = ['demo', 'me'] as const;

export const DEMO_DOC_LABEL: Record<DemoDocType, string> = { PIB: 'PIB', PEB: 'PEB', MANIFEST: 'Manifest' };

/** Whether the signed-in session is a demo account (stored at login). */
export const isDemoSession = (): boolean => {
  try {
    const raw = typeof window !== 'undefined' ? window.localStorage.getItem('authData') : null;
    return raw ? Boolean(JSON.parse(raw)?.isDemo) : false;
  } catch {
    return false;
  }
};

const errorCode = (error: unknown): string | undefined =>
  (error as AxiosError<{ error?: { code?: string } }>)?.response?.data?.error?.code;

export const isDemoExpiredError = (error: unknown) => errorCode(error) === DEMO_ERROR.expired;

/**
 * Shows the notice for a demo-specific refusal. Returns true when the error was
 * one of ours, so callers can skip their own generic "failed" message.
 */
export const notifyDemoError = (error: unknown): boolean => {
  const code = errorCode(error);
  const message = (error as AxiosError<{ error?: { message?: string } }>)?.response?.data?.error?.message;
  if (code === DEMO_ERROR.quotaExceeded) {
    notifications.show({
      id: 'demo-quota',
      title: 'Kuota akun demo habis',
      message: message || 'Kuota dokumen untuk akun demo ini sudah terpakai semua.',
      color: 'yellow',
      autoClose: 8000,
    });
    return true;
  }
  if (code === DEMO_ERROR.restricted) {
    notifications.show({
      id: 'demo-restricted',
      title: 'Akun demo',
      message: message || 'Fitur ini tidak tersedia untuk akun demo.',
      color: 'blue',
    });
    return true;
  }
  return false;
};

/** Error-path check for call sites that show their own failure toast. */
export const isDemoHandledError = (error: unknown): boolean => {
  const code = errorCode(error);
  return code === DEMO_ERROR.quotaExceeded || code === DEMO_ERROR.restricted;
};

/**
 * A demo's CEISA submission is simulated by the backend and answered as a
 * success. Tell the user so, once per response, in place of the impression
 * that it was really filed.
 */
export const notifyIfDemoSimulated = (response: AxiosResponse) => {
  if (String(response.headers?.[DEMO_SIMULATED_HEADER] ?? '') !== 'true') return;
  notifications.show({
    id: 'demo-simulated-submit',
    title: 'Akun demo',
    message: 'Dokumen tidak benar-benar dikirim ke CEISA. Pada akun berbayar, langkah ini mengirim dokumen ke CEISA.',
    color: 'blue',
    autoClose: 7000,
  });
};

/** "18j 42m" — what is left of the window, or null once it has passed. */
export const demoTimeLeft = (expiresAt: string, now = Date.now()): string | null => {
  const ms = new Date(expiresAt).getTime() - now;
  if (ms <= 0) return null;
  const minutes = Math.floor(ms / 60_000);
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours >= 24) return `${Math.floor(hours / 24)}h ${hours % 24}j`;
  if (hours > 0) return `${hours}j ${mins}m`;
  return `${mins}m`;
};
